import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { plannerPlanSelectors, plannerPlanThunks } from '@ucheba/store/planner/plan'
import { authSelectors } from '@ucheba/store/auth'
import institutionsItemThunks from '@ucheba/store/institutions/item/thunks'
import { useRouter } from 'next/router'
import { chancesOfAdmissionThunk } from '@ucheba/store/institutions/chancesOfAdmission'
import { institutionItemThunks } from '@ucheba/store/institutions/item'
import { removeQueryParameters, stringifyUrl } from '@ucheba/utils/helpers/router'
import { examsSelectors, examsThunks } from '@ucheba/store/exams'
import { getParsedCookies } from '@ucheba/utils/helpers/core'
import { authCookieToken } from '@ucheba/utils/constants/core'
import { TUseNewsId } from './types'
import { useProgramsOfficialRepresentative } from '../../../../../program/ssr/[id]/index/bll/useProgramsOfficialRepresentative'

export const useNewsId: TUseNewsId = (id) => {
  const dispatch = useDispatch()
  const uzInPlanIds = useSelector(plannerPlanSelectors.entityUzIds)
  const exam = useSelector(examsSelectors.selectAll)

  const { query, push, replace, asPath } = useRouter()

  const isAuth = useSelector(authSelectors.isValidToken)

  const isInPlanOfAdmission = useMemo(() => {
    if (!isAuth) return false

    return uzInPlanIds?.includes(id)
  }, [id, isAuth, uzInPlanIds])

  const [openTouchActions, setOpenTouchActions] = useState(false)

  const [openEducationalInstitutionAdded, setOpenEducationalInstitutionAdded] =
    useState(false)

  const handleToggleEducationalInstitutionAdded = useCallback(
    () => setOpenEducationalInstitutionAdded((prevValue) => !prevValue),
    []
  )

  const handleToggleTouchActions = useCallback(
    () => setOpenTouchActions((prevValue) => !prevValue),
    []
  )

  useEffect(() => {
    if (isAuth) {
      dispatch(
        institutionItemThunks.fetchShortChancesOfAdmission({
          data: {
            referId: query.id,
            type: 'institution',
          },
        })
      )
    }
  }, [isAuth])

  const handleWantToStudy = async (): Promise<void> => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!isAuth || !tokenString) {
      push(
        stringifyUrl(`/auth`, {
          set: {
            redirect: stringifyUrl(removeQueryParameters(asPath), {
              set: {
                ...query,
                wantToStudy: 1,
              },
            }),
          },
        })
      )

      return
    }

    if (isInPlanOfAdmission) {
      await dispatch<any>(
        plannerPlanThunks.deleteUz({
          data: {
            entityIds: [id],
          },
        })
      )
    } else {
      await dispatch<any>(
        plannerPlanThunks.postUz({
          data: {
            entityIds: [id],
            institutionId: id,
          },
        })
      )

      if (!localStorage.getItem('educationalInstitutionAddedNotification')) {
        handleToggleEducationalInstitutionAdded()
      }
    }

    await dispatch(plannerPlanThunks.getUzIds({}))
  }

  useEffect(() => {
    if (query.wantToStudy) {
      delete query.wantToStudy

      replace({ pathname: removeQueryParameters(asPath), query }, undefined, {
        shallow: true,
      })
      handleWantToStudy()
    }
  }, [])

  const { onClose: onCloseForm, onOpen, isOpen } = useProgramsOfficialRepresentative()

  const handleOpenDialog = useCallback((): void => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!isAuth || !tokenString) {
      push(
        stringifyUrl(`/auth`, {
          set: {
            redirect: stringifyUrl(removeQueryParameters(asPath), {
              set: {
                ...query,
                questionPopUp: 1,
              },
            }),
          },
        })
      )

      return
    }

    onOpen()
  }, [asPath, isAuth, onOpen, push, query])

  const onDialogClose = useCallback((): void => {
    onCloseForm()
  }, [onCloseForm])

  const [openSelectEge, setOpenSelectEge] = useState(false)
  const [openResultsAssessmentOfChances, setOpenResultsAssessmentOfChances] =
    useState(false)

  const restartAssessmentOfChances = useCallback(() => {
    setOpenResultsAssessmentOfChances(false)
    setOpenSelectEge(true)

    if (!exam.length) {
      dispatch(examsThunks.fetch({}))
    }
  }, [dispatch, exam.length])

  useEffect(() => {
    if (query.showOddsStep1 && isAuth) {
      restartAssessmentOfChances()
    }
  }, [])

  const handleToggleResultsAssessmentOfChances = useCallback(() => {
    if (query.showOddsStep1 && isAuth) {
      delete query.showOddsStep1

      replace({ pathname: removeQueryParameters(asPath), query }, undefined, {
        shallow: true,
      })
    }

    setOpenResultsAssessmentOfChances((prevValue) => !prevValue)
  }, [asPath, isAuth, query, replace])

  const handleToggleSelectEge = useCallback(() => {
    if (query.showOddsStep1 && isAuth) {
      delete query.showOddsStep1

      replace({ pathname: removeQueryParameters(asPath), query }, undefined, {
        shallow: true,
      })
    }

    setOpenSelectEge((prevValue) => !prevValue)
  }, [asPath, isAuth, query, replace])

  const handleAssessmentOfChances = async (value): Promise<void> => {
    const { achievements: achievementIds, egeList, egeExtraExam } = value

    const scores = egeList.map(({ examId, score }) => ({ examId: examId[0], score }))

    setOpenResultsAssessmentOfChances(true)
    setOpenSelectEge(false)

    await dispatch<any>(
      chancesOfAdmissionThunk.fetchChancesAdmission({
        data: {
          type: 'institution',
          referId: id,

          readyToExam: !!egeExtraExam.length,
          scores,
          achievementIds,
        },
      })
    )

    await dispatch<any>(
      institutionsItemThunks.fetchShortChancesOfAdmission({
        data: {
          type: 'institution',
          referId: id,
        },
      })
    )
  }

  return {
    isOpen,
    openSelectEge,
    openResultsAssessmentOfChances,
    isInPlanOfAdmission,
    openTouchActions,
    handleWantToStudy,
    handleToggleTouchActions,
    setOpenTouchActions,
    handleOpenDialog,
    onDialogClose,
    restartAssessmentOfChances,
    handleToggleResultsAssessmentOfChances,
    handleToggleSelectEge,
    handleAssessmentOfChances,
    openEducationalInstitutionAdded,
    handleToggleEducationalInstitutionAdded,
  }
}
